/**
 * @file MultiTenantTemplatesList.js
 * @description This page displays a list of templates (multi-tenant)
 */
import '../page-list/PageList.css';
import styles from './MultiTenantTemplatesList.module.scss';
import { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import {
    FilterModal,
    useFilter,
    renderFilterIcon,
} from '../helpers/FilterComponent';
import PaginationComponent from '../helpers/PaginationComponent';
import {
    createPortalPage,
    deletePortalPage,
    queryPortalPages,
    readPortalPage,
    updatePortalPage,
} from '../../services/portalPageService';
import { getAgencies } from '../../services/systemService';
import { getNameForDuplicate } from '../../utils/utils';
import { formatDateToMmmDDYYYYTimezone } from '../helpers/DateUtils';
import { useAuthContext } from '../../store/AuthContext';
import { ROUTE_BUILDERS } from '../../constants/routes';
import { SecurityAttributes, TenantTiers } from '../../utils/data/enums';
import { getText } from '../../i18n';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const MultiTenantTemplatesList = () => {
    const navigate = useNavigate();
    const { showModal } = useContext(ModalContext);
    const { user, hasAttribute } = useAuthContext();
    const isTemplatesReadOnly = hasAttribute(
        SecurityAttributes.MultiTenantPortalTemplatesReadOnly,
    );

    const [templates, setTemplates] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [rowsClicked, setRowsClicked] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: 'scope',
        direction: SortDirections.ASCENDING,
    });
    const [baseFilteredData, setBaseFilteredData] = useState([]);
    const [filteredData, applyFilter, clearAllFilters] =
        useFilter(baseFilteredData);
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const pageSize = 10;

    const getScopeLabel = template => {
        return (
            agencies.find(a => a.id === template.agencyId)?.name ||
            (template.agencyId === 0 ? 'Global' : template.agencyId)
        );
    };

    const getTemplate = async id => {
        const found = templates.find(t => t.id === id);
        if (found) return found;
        try {
            const payload = await queryPortalPages(false, null, true);
            return payload?.viewModels?.find(vm => vm.id === id) || null;
        } catch (err) {
            console.error('getTemplate placeholder failed', err);
            return null;
        }
    };

    const REFRACTORY_MS = 300;

    const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

    const deleteTemplates = async ids => {
        if (!Array.isArray(ids) || ids.length === 0) {
            return { succeeded: [], failed: [], details: [] };
        }

        const details = [];

        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            try {
                const result = await deletePortalPage(id);
                if (
                    result &&
                    typeof result === 'object' &&
                    'status' in result
                ) {
                    const status = result.status;
                    if (status === 200) {
                        details.push({ id, ok: true, reason: null, status });
                    } else {
                        details.push({
                            id,
                            ok: false,
                            reason: `Unexpected response status ${status}`,
                            status,
                        });
                    }
                } else {
                    details.push({ id, ok: true, reason: null, status: null });
                }
            } catch (err) {
                const status = err?.response?.status ?? null;
                const message =
                    err?.message ??
                    (status ? `HTTP ${status}` : 'Unknown error during delete');
                details.push({ id, ok: false, reason: message, status });
            }

            if (i < ids.length - 1 && REFRACTORY_MS > 0) {
                try {
                    await sleep(REFRACTORY_MS);
                } catch (sleepErr) {
                    console.warn('Sleep interrupted', sleepErr);
                }
            }
        }

        const succeeded = details.filter(d => d.ok).map(d => d.id);
        const failed = details.filter(d => !d.ok).map(d => d.id);
        return { succeeded, failed, details };
    };

    const onDeleteTemplates = async () => {
        if (isTemplatesReadOnly) {
            Toast.error(getText('msg_078913589101'));
            return;
        }

        if (!rowsClicked || rowsClicked.length === 0) {
            Toast.error(getText('msg_2be5b6754632'));
            return;
        }

        const targetIds = [...rowsClicked];
        const idToNameMap = Object.fromEntries(
            templates.map(t => [t.id, t.name ?? String(t.id)]),
        );

        try {
            const { succeeded, failed, details } =
                await deleteTemplates(targetIds);

            console.info('deleteTemplates results', {
                succeeded,
                failed,
                details,
            });

            const has405 = details.some(d => d.status === 405);

            if (succeeded.length > 0) {
                setTemplates(prev =>
                    prev.filter(t => !succeeded.includes(t.id)),
                );
            }

            setRowsClicked(prev => prev.filter(id => failed.includes(id)));

            const failedNames = failed.map(
                fid => idToNameMap[fid] ?? String(fid),
            );

            if (failed.length === 0) {
                Toast.success(
                    `${succeeded.length > 1 ? 'Templates' : 'Template'} deleted successfully`,
                );
                setRowsClicked([]);
            } else {
                if (has405) {
                    Toast.error(getText('msg_cf09950c1795'));
                }

                if (succeeded.length > 0) {
                    Toast.success(
                        `${succeeded.length > 1 ? 'Templates' : 'Template'} deleted successfully`,
                    );
                    Toast.error(
                        `Failed to delete the following templates: ${failedNames.join(', ')}.`,
                    );
                } else {
                    // None succeeded
                    Toast.error(
                        `Failed to delete the following templates: ${failedNames.join(', ')}.`,
                    );
                }
            }
        } catch (err) {
            console.error('Unexpected error in onDeleteTemplates', err);
            Toast.error(getText('msg_117575e8634e'));
        }
    };

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));

        if (column === 'scope') {
            const mappedIds = (selectedOptions || []).map(opt => {
                if (opt === 'Global') return 0;
                const match = agencies.find(a => a.name === opt);
                return match ? match.id : opt;
            });

            applyFilter('agencyId', mappedIds);
            return;
        }

        applyFilter(column, selectedOptions);
    };

    const clearFilters = () => {
        setSelections({});
        setSearchTerm('');
        clearAllFilters();
    };

    const getOptions = column => {
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column];

        const filtered = templates.filter(item =>
            Object.entries(filtersWithoutCurrent).every(([key, values]) =>
                values.length === 0
                    ? true
                    : (() => {
                          if (key === 'scope') {
                              return values.includes(getScopeLabel(item));
                          }
                          return values.includes(item[key]);
                      })(),
            ),
        );

        if (column === 'scope') {
            return Array.from(
                new Set(filtered.map(item => getScopeLabel(item))),
            )
                .filter(Boolean)
                .sort((a, b) => {
                    const first = (a || '').toString().toLowerCase();
                    const second = (b || '').toString().toLowerCase();

                    if (first === 'global' && second !== 'global') return -1;
                    if (first !== 'global' && second === 'global') return 1;

                    return first.localeCompare(second);
                });
        }

        return Array.from(new Set(filtered.map(item => item[column])))
            .filter(Boolean)
            .sort((a, b) => {
                if (typeof a === 'string' && typeof b === 'string') {
                    return a.localeCompare(b);
                }
                return a - b;
            });
    };

    const fetchTemplates = async () => {
        setIsLoading(true);
        try {
            const payload = await queryPortalPages(true, 0, true);
            if (!payload) {
                console.warn('queryPortalPages returned empty payload');
                setTemplates([]);
                setTotalPages(1);
                return;
            }
            const viewModels = Array.isArray(payload.viewModels)
                ? payload.viewModels
                : [];
            setTemplates(viewModels);
            setTotalPages(payload.pageTotal || 1);
        } catch (error) {
            console.error('Failed to fetch templates', error);
            Toast.error(getText('msg_d55a44d5b394'));
            setTemplates([]);
            setTotalPages(1);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAgencies = async () => {
        try {
            const fetchedAgencies = await getAgencies();
            setAgencies(fetchedAgencies.viewModels || []);
        } catch (error) {
            console.error('Failed to fetch agencies', error);
            Toast.error(getText('msg_27ded7882fa5'));
        }
    };

    useEffect(() => {
        fetchTemplates();
        fetchAgencies();
    }, [currentPage]);

    useEffect(() => {
        const searchFiltered = templates.filter(t =>
            t.name?.toLowerCase().includes(searchTerm.toLowerCase()),
        );

        const compareAlphabetically = (left, right) => {
            return (left || '').localeCompare(right || '', undefined, {
                sensitivity: 'base',
            });
        };

        const sorted = [...searchFiltered].sort((a, b) => {
            const key = sortConfig.key;
            const dir =
                sortConfig.direction === SortDirections.ASCENDING ? 1 : -1;

            if (key === 'scope') {
                const aScopeLabel = (getScopeLabel(a) || '').toString();
                const bScopeLabel = (getScopeLabel(b) || '').toString();

                const aIsGlobal =
                    a.agencyId === 0 || aScopeLabel.toLowerCase() === 'global';
                const bIsGlobal =
                    b.agencyId === 0 || bScopeLabel.toLowerCase() === 'global';

                if (aIsGlobal !== bIsGlobal) {
                    return (aIsGlobal ? -1 : 1) * dir;
                }

                const scopeCompare =
                    compareAlphabetically(aScopeLabel, bScopeLabel) * dir;
                if (scopeCompare !== 0) {
                    return scopeCompare;
                }

                return compareAlphabetically(a.name, b.name);
            }

            const getValue = item => {
                switch (key) {
                    case 'name':
                        return (item.name || '').toLowerCase();
                    case 'bronze':
                        return item.availableTierIds?.includes(
                            TenantTiers.Bronze,
                        )
                            ? 1
                            : 0;
                    case 'base':
                        return item.availableTierIds?.includes(TenantTiers.Base)
                            ? 1
                            : 0;
                    case 'scope':
                        return (getScopeLabel(item) || '').toLowerCase();
                    default:
                        return (item[key] || '').toString().toLowerCase();
                }
            };

            const va = getValue(a);
            const vb = getValue(b);

            if (typeof va === 'number' && typeof vb === 'number') {
                return (va - vb) * dir;
            }
            if (va < vb) return -1 * dir;
            if (va > vb) return 1 * dir;
            return 0;
        });

        setBaseFilteredData(sorted);
    }, [templates, searchTerm, sortConfig, agencies]);

    const handleSearch = event => {
        setSearchTerm(event.target.value);
    };

    const toggleSort = key => {
        if (sortConfig.key === key) {
            setSortConfig({
                key,
                direction:
                    sortConfig.direction === SortDirections.ASCENDING
                        ? SortDirections.DESCENDING
                        : SortDirections.ASCENDING,
            });
        } else {
            setSortConfig({ key, direction: SortDirections.ASCENDING });
        }
    };

    const handleSortName = () => toggleSort('name');
    const handleSortBronze = () => toggleSort('bronze');
    const handleSortBase = () => toggleSort('base');
    const handleSortLastEdited = () => toggleSort('updatedAt');
    const handleSortEditedBy = () => toggleSort('updatedBy');
    const handleSortScope = () => toggleSort('scope');
    const handleSortEnableStatus = () => toggleSort('isLocked');

    const handlerRowClicked = templateId => {
        setRowsClicked(prevSelection =>
            prevSelection.includes(templateId)
                ? prevSelection.filter(id => id !== templateId)
                : [...prevSelection, templateId],
        );
    };

    const onToggle = async id => {
        if (isTemplatesReadOnly) {
            Toast.error(getText('msg_1db7de624f57'));
            return;
        }

        try {
            const template = await getTemplate(id);
            if (!template) throw new Error('Template not found');

            const newStatus = template.status === 1 ? 2 : 1;
            const payload = { ...template, status: newStatus };

            const fetchedTemplate = await updatePortalPage(payload);

            const resultingStatus = fetchedTemplate?.status ?? newStatus;

            const copyTemplates = templates.map(t => ({ ...t }));

            const idx = copyTemplates.findIndex(
                t => String(t.id) === String(id),
            );
            if (idx === -1) {
                console.warn(
                    'onToggle: template id not found in local templates',
                    id,
                );
            } else {
                copyTemplates[idx] = {
                    ...copyTemplates[idx],
                    ...fetchedTemplate,
                    status: resultingStatus,
                };

                setTemplates(copyTemplates);
            }

            Toast.success(
                resultingStatus === 1
                    ? 'The template was enabled succesfully.'
                    : 'The template was disabled succesfully.',
            );
        } catch (err) {
            console.error('Failed to toggle template enable status', err);
            Toast.error(getText('msg_9b2a253076c9'));
        }
    };

    const onDuplicateTemplates = async () => {
        if (isTemplatesReadOnly) {
            Toast.error(getText('msg_f01af2b1a961'));
            return;
        }

        if (!rowsClicked || rowsClicked.length === 0) {
            Toast.error(getText('msg_d5b78eba52c2'));
            return;
        }

        const created = [];
        const failed = [];

        const currentNames = templates.map(t => t.name).filter(Boolean);

        const getNameForDuplicateFallback = (baseName, existingNames = []) => {
            const base = (baseName || 'Untitled').trim();
            const existing = new Set(existingNames.filter(Boolean));
            if (!existing.has(base)) return base;
            const tryBase = `${base} (Copy)`;
            if (!existing.has(tryBase)) return tryBase;
            for (let k = 2; k <= 1000; k++) {
                const candidate = `${base} (Copy ${k})`;
                if (!existing.has(candidate)) return candidate;
            }
            let suffix = 1;
            while (existing.has(`${base} (${suffix})`)) suffix++;
            return `${base} (${suffix})`;
        };

        const nameGenerator =
            typeof getNameForDuplicate === 'function'
                ? getNameForDuplicate
                : getNameForDuplicateFallback;

        const delayMs = typeof REFRACTORY_MS === 'number' ? REFRACTORY_MS : 0;

        try {
            for (let i = 0; i < rowsClicked.length; i++) {
                const id = rowsClicked[i];
                try {
                    const original = await readPortalPage(id, true);
                    const vm = original?.viewModels?.[0];
                    if (!vm) throw new Error(`Template not found for id ${id}`);

                    const { id: _omit, ...payload } = vm;
                    const newName = nameGenerator(vm.name, currentNames);
                    payload.name = newName;

                    if (i < rowsClicked.length - 1 && delayMs > 0) {
                        await sleep(delayMs);
                    }

                    const rawCreated = await createPortalPage(payload);
                    if (!rawCreated)
                        throw new Error(
                            `createPortalPage returned falsy for id ${id}`,
                        );

                    const createdItem =
                        (rawCreated &&
                            rawCreated.viewModels &&
                            rawCreated.viewModels[0]) ||
                        rawCreated;

                    if (!createdItem || createdItem.id == null) {
                        console.error('Unexpected created template shape', {
                            rawCreated,
                        });
                        throw new Error(
                            'Unexpected created template shape from API',
                        );
                    }

                    created.push(createdItem);
                    currentNames.push(newName);
                } catch (err) {
                    console.error(`Failed to duplicate template id ${id}`, err);
                    const name =
                        templates.find(t => String(t.id) === String(id))
                            ?.name || String(id);
                    failed.push({ id, name, error: err });
                }
            }

            if (created.length > 0) {
                let copyOfTemplates;
                try {
                    if (typeof structuredClone === 'function') {
                        copyOfTemplates = structuredClone(templates);
                    } else {
                        copyOfTemplates = JSON.parse(JSON.stringify(templates));
                    }
                } catch (cloneErr) {
                    console.warn(
                        'Deep clone failed, falling back to shallow copy',
                        cloneErr,
                    );
                    copyOfTemplates = templates.map(t => ({ ...t }));
                }

                const toInsert = created.slice().reverse();

                toInsert.forEach(ct => copyOfTemplates.unshift(ct));

                setTemplates(prev => {
                    if (prev === copyOfTemplates) {
                        return copyOfTemplates;
                    }
                    const existingIds = new Set(created.map(c => String(c.id)));
                    const filteredPrev = prev.filter(
                        p => !existingIds.has(String(p.id)),
                    );
                    return [...toInsert, ...filteredPrev];
                });
            }

            if (failed.length === 0) {
                Toast.success(
                    `${created.length > 1 ? 'Templates' : 'Template'} duplicated successfully`,
                );
                setRowsClicked([]);
            } else {
                const failedNames = failed.map(f => f.name);
                if (created.length > 0) {
                    Toast.success(
                        `${created.length} ${created.length > 1 ? 'templates' : 'template'} duplicated`,
                    );
                    Toast.error(
                        `Failed to duplicate: ${failedNames.join(', ')}.`,
                    );
                } else {
                    Toast.error(
                        `Failed to duplicate the following templates: ${failedNames.join(', ')}.`,
                    );
                }
            }
        } catch (err) {
            console.error('Unexpected error during duplication', err);
            Toast.error(getText('msg_659f2b552df7'));
        }
    };

    const rowAction = async action => {
        if (isTemplatesReadOnly) {
            Toast.error(getText('msg_ababfe94a870'));
            return;
        }

        if (rowsClicked.length > 0) {
            switch (action) {
                case 'delete': {
                    let canBeDeleted = true;
                    for (let index = 0; index < rowsClicked.length; index++) {
                        const templateId = rowsClicked[index];
                        const fetchedTemplate = await getTemplate(templateId);
                        if (
                            fetchedTemplate &&
                            fetchedTemplate.hasOwnProperty('canBeDeleted')
                        ) {
                            if (!fetchedTemplate.canBeDeleted) {
                                canBeDeleted = false;
                                break;
                            }
                        }
                    }
                    if (canBeDeleted) {
                        showModal({
                            type: ModalTypes.DELETE_CONFIRMATION,
                            title: getText('msg_b957efeb6067'),
                            text: `This action will delete ${rowsClicked.length > 1 ? 'all selected templates' : 'this template'}.`,
                            onYes: () => onDeleteTemplates(),
                            onNo: () => {},
                            noButtonText: getText('msg_77dfd2135f4d'),
                            yesButtonText: getText('msg_f6fdbe48dc54'),
                        });
                    } else {
                        showModal({
                            type: ModalTypes.ACTION,
                            title: getText('msg_b78130c92f32'),
                            subtitle: `${rowsClicked.length > 1 ? 'One or more of these templates are' : 'This template is'} currently in use`,
                            text: getText('msg_b32b4f3b7856'),
                            onYes: () => {},
                            onNo: () => {},
                            noButtonText: getText('msg_77dfd2135f4d'),
                            yesButtonText: getText('msg_2e02623966f9'),
                        });
                    }
                    break;
                }
                case 'duplicate': {
                    showModal({
                        type: ModalTypes.ACTION,
                        title: getText('msg_5ebb09c8ea76'),
                        subtitle: '',
                        text: `This action will create copies of the ${rowsClicked.length > 1 ? 'selected templates' : 'selected template'}.`,
                        onYes: () => onDuplicateTemplates(),
                        onNo: () => {},
                        noButtonText: getText('msg_77dfd2135f4d'),
                        yesButtonText: getText('msg_972d57379db3'),
                    });
                    break;
                }
                default: {
                    break;
                }
            }
        } else {
            Toast.error(getText('msg_9bcf67dc54d5'));
        }
    };

    if (isLoading) {
        return <Spinner width={50} className={styles.loadingSpinner} />;
    }

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    {/* Screen Header with Title and Actions */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        {/* Screen Title */}
                        <div className="float-start d-flex flex-direction-column">
                            <p className="page-title card-title ms-4 me-5">
                                {getText('msg_bbdab48cf73c')}
                            </p>
                            <p
                                className="ing-portals-subtitle mt-1"
                                style={{ marginTop: '10px' }}
                            >
                                {getText('msg_236951fd9321')}
                            </p>
                        </div>
                        {/* Screen Actions */}
                        <div className="float-end d-flex align-items-center">
                            {!isTemplatesReadOnly && (
                                <>
                                    <span
                                        type="button"
                                        onClick={() => rowAction('delete')}
                                        className="ing-text-buttons mx-3"
                                    >
                                        <i className="bi bi-trash3 me-2" />
                                        {getText('msg_f6fdbe48dc54')}
                                    </span>
                                    <span
                                        type="button"
                                        onClick={() => rowAction('duplicate')}
                                        className="ing-text-buttons mx-3"
                                    >
                                        <i className="bi bi-copy me-2" />
                                        {getText('msg_972d57379db3')}
                                    </span>
                                </>
                            )}
                            <span
                                type="button"
                                onClick={clearFilters}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                {getText('msg_381c4bf1d409')}
                            </span>
                            {!isTemplatesReadOnly && (
                                <span
                                    onClick={() =>
                                        navigate('new-template', {
                                            state: {
                                                loadFromMultiTenantTemplates: true,
                                            },
                                        })
                                    }
                                    type="button"
                                    className="ms-3 me-4  button-base ing-button-primary-left align-middle"
                                >
                                    <i className="bi bi-plus-lg me-1" />
                                    {getText('msg_61cc55aa0453')}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="page-title card-title ms-4 me-5 mb-5">
                        <div className={styles.searchWrapper}>
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder={getText('msg_8048de160731')}
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                            <span className={styles.searchIcon}>
                                <i className="bi bi-search"></i>
                            </span>
                        </div>
                    </div>

                    <div className="table-responsive-xxl w-100">
                        {/* Template List Table */}
                        <table className="table table-hover align-middle">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    <th className="text-center">
                                        <svg
                                            width="19"
                                            height="19"
                                            viewBox="0 0 19 19"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M5.2 9.3H12.8"
                                                stroke="#0070FF"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <rect
                                                x="0.5"
                                                y="0.5"
                                                width="17"
                                                height="17"
                                                rx="4"
                                                stroke="#0070FF"
                                            />
                                        </svg>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_74172fb9d7c8')}
                                        </span>
                                        <span
                                            onClick={handleSortName}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'name' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_4651a34e4df9')}
                                        </span>
                                        <span
                                            onClick={handleSortScope}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'scope' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={() => openModal('scope')}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['scope']?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'scope' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th className="text-center">
                                        <span className="table-column-header-text">
                                            {getText('msg_af3102527f03')}
                                        </span>
                                        <span
                                            onClick={handleSortBronze}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'bronze' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                    <th className="text-center">
                                        <span className="table-column-header-text">
                                            {getText('msg_077fe9c54ed5')}
                                        </span>
                                        <span
                                            onClick={handleSortBase}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'base' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_0c79adcf41ec')}
                                        </span>
                                        <span
                                            onClick={handleSortLastEdited}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'updatedAt' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_6180d5db3ed9')}
                                        </span>
                                        <span
                                            onClick={handleSortEditedBy}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'updatedBy' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                    <th className="text-center">
                                        <span className="table-column-header-text">
                                            {getText('msg_20063ad90532')}
                                        </span>
                                        <span
                                            onClick={handleSortEnableStatus}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'isLocked' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.map(template => (
                                    <tr
                                        key={template.id}
                                        className={`${
                                            rowsClicked.includes(template.id)
                                                ? 'clicked-class'
                                                : ''
                                        }`}
                                    >
                                        <td className="text-center">
                                            <input
                                                id={`tpl-${template.id}`}
                                                onClick={() =>
                                                    handlerRowClicked(
                                                        template.id,
                                                    )
                                                }
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={rowsClicked.includes(
                                                    template.id,
                                                )}
                                                disabled={isTemplatesReadOnly}
                                                readOnly={isTemplatesReadOnly}
                                            />
                                        </td>
                                        <td>
                                            <span
                                                onClick={() => {
                                                    if (isTemplatesReadOnly) {
                                                        navigate(
                                                            ROUTE_BUILDERS.portalTemplatesPreview(
                                                                template.id,
                                                            ),
                                                            {
                                                                state: {
                                                                    template,
                                                                    fromMultiTenant: true,
                                                                },
                                                            },
                                                        );
                                                        return;
                                                    }

                                                    const userAgencies =
                                                        user?.jwt?.agencies ||
                                                        [];
                                                    const isGlobalTemplate =
                                                        template.agencyId === 0;
                                                    const hasGlobalAccess =
                                                        userAgencies.includes(
                                                            0,
                                                        );

                                                    if (
                                                        isGlobalTemplate &&
                                                        !hasGlobalAccess
                                                    ) {
                                                        navigate(
                                                            ROUTE_BUILDERS.portalTemplatesPreview(
                                                                template.id,
                                                            ),
                                                            {
                                                                state: {
                                                                    template,
                                                                    fromMultiTenant: true,
                                                                },
                                                            },
                                                        );
                                                        return;
                                                    }

                                                    navigate('new-template', {
                                                        state: {
                                                            loadFromMultiTenantTemplates: true,
                                                            id: template.id,
                                                        },
                                                    });
                                                }}
                                                className="titleCell"
                                            >
                                                {template.name}
                                            </span>
                                        </td>
                                        <td className="subtitleCell">
                                            {getScopeLabel(template)}
                                        </td>
                                        <td className="text-center subtitleCell">
                                            {template.availableTierIds?.includes(
                                                TenantTiers.Bronze,
                                            )
                                                ? 'Y'
                                                : '-'}
                                        </td>
                                        <td className="text-center subtitleCell">
                                            {template.availableTierIds?.includes(
                                                TenantTiers.Base,
                                            )
                                                ? 'Y'
                                                : '-'}
                                        </td>
                                        <td className="subtitleCell">
                                            {formatDateToMmmDDYYYYTimezone(
                                                template.updatedAt ||
                                                    template.createdAt,
                                            )}
                                        </td>
                                        <td className="subtitleCell">
                                            {template.updatedBy ||
                                                template.createdBy ||
                                                '-'}
                                        </td>
                                        <td className="text-center">
                                            <div className={`form-switch`}>
                                                <input
                                                    onChange={() =>
                                                        onToggle(template.id)
                                                    }
                                                    className="form-check-input custom-switch-input"
                                                    type="checkbox"
                                                    checked={
                                                        template.status === 1
                                                    }
                                                    disabled={
                                                        isTemplatesReadOnly
                                                    }
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {(templates == null || templates.length < 1) && (
                            <NotFoundBox
                                backgroundColor="#f0f4ff"
                                title={getText('msg_469b72190312')}
                                description={getText('msg_5a558f8e8fb9')}
                            />
                        )}

                        {totalPages > 1 && (
                            <div className="mt-auto">
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MultiTenantTemplatesList;
